import { useState, useEffect } from 'react';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import {
  parseTransportVerbatimMessage,
  extractTransportAddressFromVerbatim,
  type TransportVerbatimMessage,
  type EtlTransportResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { LAUNCH_TYPES } from '../../AirAmbulanceForm/constants';
import { useFormReverseGeocode } from '../../AirAmbulanceForm/hooks';
import { formatAddressFromObject } from '../../AirAmbulanceForm/utils';
import {
  formatCoordinates,
  parseCoordinates,
} from '../../AirAmbulanceForm/validation';
import type { Coordinates, SelectOption } from '../../types';

export interface EditFormValues {
  pickup_coordinates: string;
  pickup_location: string;
  pickup_details: string;
  ground_contact: string;
  patient_info: string;
  request_reason: SelectOption | string;
  multiple_units_requested: string;
  launch_type: string;
  air_service_declined: string;
  air_service_responding: string;
  landing_zone_external_id: string;
  [key: string]: string | SelectOption;
}

/**
 * Derive Formik initial values from a transport, including reverse geocoding
 * the pickup address if only coordinates are available.
 */
export function useEditFormValues(
  transport: EtlTransportResponse,
): EditFormValues {
  const verbatim = parseTransportVerbatimMessage(transport.verbatim_message);
  const baseValues = getInitialValues(verbatim, transport.is_test);

  const service = useWhichReverseGeocodeService();
  const [coordsToReverseGeocode, setCoordsToReverseGeocode] =
    useState<Coordinates | null>(null);
  const { foundAddress } = useFormReverseGeocode({
    coordinates: coordsToReverseGeocode,
    service,
  });

  const [reverseGeocodedAddress, setReverseGeocodedAddress] = useState('');

  useEffect(() => {
    if (!baseValues.pickup_location && baseValues.pickup_coordinates) {
      const coords = parseCoordinates(baseValues.pickup_coordinates);
      if (coords) {
        setCoordsToReverseGeocode(coords);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  useEffect(() => {
    if (foundAddress) {
      const formatted = foundAddress.addressObject
        ? formatAddressFromObject(foundAddress.addressObject)
        : foundAddress.address || '';
      setReverseGeocodedAddress(formatted);
    }
  }, [foundAddress]);

  return {
    ...baseValues,
    pickup_location: baseValues.pickup_location || reverseGeocodedAddress,
  };
}

function getInitialValues(
  verbatim: TransportVerbatimMessage | undefined,
  isTest: boolean,
): EditFormValues {
  if (!verbatim) {
    return {
      pickup_coordinates: '',
      pickup_location: '',
      pickup_details: '',
      ground_contact: '',
      patient_info: '',
      request_reason: 'Select',
      multiple_units_requested: 'No',
      launch_type: 'Immediate',
      air_service_declined: '',
      air_service_responding: '',
      landing_zone_external_id: '',
    };
  }

  const tripLeg = verbatim.tripLeg?.[0];
  const pickupLocation = tripLeg?.pickupLocation;
  const airDetails = tripLeg?.airDetails;

  const lat = pickupLocation?.latitude;
  const lng = pickupLocation?.longitude;
  // pickupLocation.comment is the pickup DETAILS (maps from API's pickupLocationComments)
  const pickupDetails = pickupLocation?.comment ?? '';

  const coords = formatCoordinates(lat, lng);

  const { tripReason, comments } = verbatim;
  const idenStandBy = airDetails?.idenStandBy;

  let launchType: string;
  if (isTest) {
    launchType = LAUNCH_TYPES.TEST;
  } else if (idenStandBy) {
    launchType = LAUNCH_TYPES.STANDBY;
  } else {
    launchType = LAUNCH_TYPES.IMMEDIATE;
  }

  const callerAddress = extractTransportAddressFromVerbatim(verbatim) ?? '';

  return {
    pickup_coordinates: coords,
    pickup_location: callerAddress,
    pickup_details: pickupDetails,
    ground_contact: airDetails?.groundContact ?? '',
    patient_info: comments ?? '',
    request_reason: tripReason
      ? { value: tripReason, label: tripReason }
      : 'Select',
    multiple_units_requested: tripLeg?.multipleUnits ? 'Yes' : 'No',
    launch_type: launchType,
    air_service_declined: airDetails?.svcWeatherDecline ? 'Yes' : 'No',
    air_service_responding: airDetails?.svcOtherRespond ? 'Yes' : 'No',
    landing_zone_external_id: '',
  };
}
